/**
 * DocxBlobPreview - renders a .docx Blob faithfully (real fonts/colors/layout)
 * via the lazy-loaded `docx-preview`. Extracted so both the asset preview and the
 * SOW studio's Proof canvas share one renderer. Page-accurate by default (a page
 * sheet); pass `fit` to flow the content to the container width.
 */

import * as React from 'react';

const DOCX_CLASS = 'ai-file-preview-docx';

/** Scroll the first rendered line matching `heading` into view (silent no-op otherwise). */
export function scrollToHeadingIn(host: HTMLElement, heading: string): void {
  const target = heading.trim().toLowerCase();
  if (!target) return;
  const lines = host.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, p');
  for (const el of Array.from(lines)) {
    const text = (el.textContent ?? '').trim().toLowerCase();
    if (text && (text === target || text.startsWith(target))) {
      el.scrollIntoView({ block: 'start' });
      return;
    }
  }
}

export interface DocxBlobPreviewProps {
  blob: Blob | null;
  /** Flow to the container width instead of a fixed page sheet. */
  fit?: boolean;
  /** Scroll to a heading after render. */
  scrollToHeading?: string;
}

export function DocxBlobPreview({ blob, fit = false, scrollToHeading }: DocxBlobPreviewProps) {
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [status, setStatus] = React.useState<'idle' | 'rendering' | 'ready' | 'error'>('idle');

  React.useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    if (!host || !blob) return;
    host.innerHTML = '';
    setStatus('rendering');
    void (async () => {
      try {
        const { renderAsync } = await import('docx-preview');
        if (cancelled || !hostRef.current) return;
        await renderAsync(blob, hostRef.current, undefined, {
          className: DOCX_CLASS,
          inWrapper: !fit,
          ignoreWidth: !!fit,
          ignoreHeight: !!fit,
          breakPages: true,
          renderHeaders: true,
          renderFooters: true,
        });
        if (cancelled || !hostRef.current) return;
        if (scrollToHeading) scrollToHeadingIn(hostRef.current, scrollToHeading);
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blob, fit, scrollToHeading]);

  return (
    <>
      {status === 'rendering' ? (
        <p className="ai-file-preview__status">Rendering document…</p>
      ) : null}
      {status === 'error' ? (
        <p className="ai-file-preview__status">This document could not be rendered.</p>
      ) : null}
      <div ref={hostRef} className="ai-file-preview__docx-host" />
    </>
  );
}
